import { useQuery } from '@tanstack/react-query';
import axios from '../config/axios';
import { Device } from '../types/device.types';
import { Installment } from '../types/installment.types';
import { Badge, Button, Table, Tag, Typography, Modal, Form, Input, message } from 'antd';
import { ColumnType, TablePaginationConfig } from 'antd/es/table';
import { formatNumber, getColorByStatus } from '../utils/helpers';
import dayjs from '../config/dayjs';
import CopyableText from './copyable-text';
import { CalendarOutlined } from '@ant-design/icons';
import { AuthContext } from '../context/auth.context';
import { useContext, useState } from 'react';
import { AxiosError } from 'axios';
import { ContractAmendmentHistory } from './contract-amendments';

interface DeviceInstallmentsProps {
	device: Device;
}

export const DeviceInstallments = ({ device }: DeviceInstallmentsProps) => {
	const { role } = useContext(AuthContext);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
	const [selectedInstallment, setSelectedInstallment] = useState<Installment | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [form] = Form.useForm();
	const [isWaivePenaltyModalVisible, setIsWaivePenaltyModalVisible] = useState(false);
	const [isWaivingPenalty, setIsWaivingPenalty] = useState(false);
	const [waivePenaltyForm] = Form.useForm();

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['device-installments', device.id, currentPage, pageSize],
		queryFn: async () => {
			const response = await axios.get(role === 'admin' ? `/admin/devices/installments` : `/retailers/devices/installments`, {
				withCredentials: true,
				params: {
					device_id: device.id,
					page: currentPage,
					limit: pageSize,
				},
			});
			return response.data;
		},
	});

	const installments: Installment[] = data?.results || [];
	const totalCount = data?.count || 0;

	const showPaymentModal = (installment: Installment) => {
		setSelectedInstallment(installment);
		form.setFieldsValue({ amount: parseFloat(installment.outstanding_amount) });
		setIsPaymentModalVisible(true);
	};

	const handleCancel = () => {
		setIsPaymentModalVisible(false);
		form.resetFields();
	};

	const handleInitPayment = async (values: { phone_number: string; amount: number }) => {
		if (!selectedInstallment) return;

		setIsSubmitting(true);
		try {
			await axios.post(
				`/admin/payments/installments/${selectedInstallment.id}/pay`,
				{
					phone_number: values.phone_number,
					amount: values.amount,
				},
				{
					withCredentials: true,
				},
			);

			message.success('Payment initiated successfully');
			setIsPaymentModalVisible(false);
			form.resetFields();
			refetch();
		} catch (error: unknown) {
			console.error('Failed to initiate payment:', error);
			if (error instanceof AxiosError) {
				message.error(error.response?.data.msg || 'Failed to initiate payment');
			} else {
				message.error('Failed to initiate payment');
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	const showWaivePenaltyModal = (installment: Installment) => {
		setSelectedInstallment(installment);
		setIsWaivePenaltyModalVisible(true);
	};

	const handleWaivePenaltyCancel = () => {
		setIsWaivePenaltyModalVisible(false);
		waivePenaltyForm.resetFields();
	};

	const handleWaivePenalty = async (values: { amount: string }) => {
		if (!selectedInstallment) return;

		setIsWaivingPenalty(true);
		try {
			await axios.post(
				`/admin/super/devices/installments/${selectedInstallment.id}/waive-penalty`,
				{
					amount: values.amount,
				},
				{
					withCredentials: true,
				},
			);

			message.success('Penalty waived successfully');
			setIsWaivePenaltyModalVisible(false);
			waivePenaltyForm.resetFields();
			refetch();
		} catch (error: unknown) {
			console.error('Failed to waive penalty:', error);
			if (error instanceof AxiosError) {
				message.error(error.response?.data.msg || 'Failed to waive penalty');
			} else {
				message.error('Failed to waive penalty');
			}
		} finally {
			setIsWaivingPenalty(false);
		}
	};

	const columns: ColumnType<Installment>[] = [
		{
			title: 'id',
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: 'Installment Number',
			dataIndex: 'installment_number',
			key: 'installment_number',
			render: (installment_number: number, installment: Installment) => {
				return (
					<div className="flex items-center gap-2">
						<Badge
							count={installment_number}
							color="blue"
						/>
						{installment.is_down_payment && (
							<Badge
								status="success"
								text="Down Payment"
							/>
						)}
					</div>
				);
			},
		},
		{
			title: 'Amount',
			dataIndex: 'amount',
			key: 'amount',
			render: (amount: number, installment: Installment) => {
				const paid = parseFloat(installment.paid_amount);
				const outstanding = parseFloat(installment.outstanding_amount);
				if (paid > 0 && installment.status !== 'paid') {
					return (
						<div className="flex flex-col gap-1">
							<span>{formatNumber(amount)}</span>
							<Typography.Text type="secondary" className="text-xs">
								Paid: <span className="text-green-600 font-medium">{formatNumber(paid)}</span>
							</Typography.Text>
							<Typography.Text type="secondary" className="text-xs">
								Outstanding: <span className="text-orange-500 font-medium">{formatNumber(outstanding)}</span>
							</Typography.Text>
						</div>
					);
				}
				return <span>{formatNumber(amount)}</span>;
			},
		},
		{
			title: 'Penalty Amount',
			dataIndex: 'penalty_amount',
			key: 'penalty_amount',
			render: (penalty_amount: string) => {
				if (!penalty_amount || parseFloat(penalty_amount) <= 0) return '-';
				return <Typography.Text type="danger">{formatNumber(penalty_amount)} UGX</Typography.Text>;
			},
		},
		{
			title: 'Due Date',
			dataIndex: 'due_date',
			key: 'due_date',
			render: (due_date: string) => dayjs(due_date).format('DD MMM YYYY'),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status: string, installment: Installment) => {
				const doublePaid = installment.double_paid_amount && (
					<Tag color="red">DOUBLE PAYMENT UGX {formatNumber(installment.double_paid_amount)}</Tag>
				);
				// Add special styling for overdue installments
				if (status === 'pending' && dayjs(installment.due_date).isBefore(dayjs(), 'day')) {
					return (
						<div className="flex items-center gap-2">
							<Tag color="red">OVERDUE</Tag>
							<Badge status="error" />
							{doublePaid}
						</div>
					);
				}
				return (
					<div className="flex items-center gap-2">
						<Tag color={getColorByStatus(status)}>{status.toUpperCase()}</Tag>
						{doublePaid}
					</div>
				);
			},
		},
		{
			title: 'Transaction',
			dataIndex: 'id',
			key: 'id',
			render: (_: number, installment: Installment | null) => {
				if (!installment?.transaction) return 'N/A';
				return (
					<div className="rounded-md border border-gray-200 bg-gray-50 p-3">
						<div className="flex flex-col gap-3">
							<div className="flex items-center justify-between">
								<Typography.Text strong>Transaction ID:</Typography.Text>
								<CopyableText text={installment?.transaction?.id?.toString() || ''}>
									<Typography.Text code>{installment?.transaction?.id}</Typography.Text>
								</CopyableText>
							</div>

							<div className="flex items-center justify-between">
								<Typography.Text strong>Status:</Typography.Text>
								<Tag
									color={
										installment?.transaction?.status === 'success'
											? 'green'
											: installment?.transaction?.status === 'pending'
												? 'orange'
												: installment?.transaction?.status === 'failed'
													? 'red'
													: 'default'
									}
								>
									{installment?.transaction?.status?.toUpperCase()}
								</Tag>
							</div>

							<div className="flex items-center justify-between">
								<Typography.Text strong>Gateway:</Typography.Text>
								<Tag
									color={
										installment?.transaction?.gateway === 'beyonic'
											? 'purple'
											: installment?.transaction?.gateway === 'yo'
												? 'blue'
												: installment?.transaction?.gateway === 'cash in hand'
													? 'green'
													: 'default'
									}
								>
									{installment?.transaction?.gateway?.replace('_', ' ').toUpperCase()}
								</Tag>
							</div>

							<div className="flex items-center justify-between">
								<Typography.Text strong>Gateway TRX ID:</Typography.Text>
								{(() => {
									if (installment?.transaction?.gateway === 'beyonic') {
										return installment?.transaction?.gateway_trx_id ? (
											<CopyableText text={installment.transaction.gateway_trx_id.toString()}>
												<Typography.Text code>{installment.transaction.gateway_trx_id}</Typography.Text>
											</CopyableText>
										) : (
											<Typography.Text type="secondary">N/A</Typography.Text>
										);
									} else if (installment?.transaction?.gateway === 'yo') {
										const yoReference = (installment?.transaction?.metadata as { yo_external_reference?: string })
											?.yo_external_reference;
										return yoReference ? (
											<CopyableText text={yoReference.toString()}>
												<Typography.Text code>{yoReference}</Typography.Text>
											</CopyableText>
										) : (
											<Typography.Text type="secondary">N/A</Typography.Text>
										);
									} else {
										return installment?.transaction?.gateway_trx_id ? (
											<CopyableText text={installment.transaction.gateway_trx_id.toString()}>
												<Typography.Text code>{installment.transaction.gateway_trx_id}</Typography.Text>
											</CopyableText>
										) : (
											<Typography.Text type="secondary">N/A</Typography.Text>
										);
									}
								})()}
							</div>

							<div className="flex items-center justify-between">
								<Typography.Text strong>Date:</Typography.Text>
								<div className="flex items-center gap-1">
									<CalendarOutlined className="text-gray-400" />
									<Typography.Text type="secondary">
										{dayjs(installment?.transaction?.date).format('DD MMM YYYY - LT')}
									</Typography.Text>
								</div>
							</div>
						</div>
					</div>
				);
			},
		},
		{
			title: 'Actions',
			key: 'actions',
			render: (_, installment: Installment) => {
				const penaltyAmount = installment.penalty_amount ? parseInt(installment.penalty_amount) : 0;
				const hasPenalty = penaltyAmount > 0;

				return (
					<div className="flex gap-2">
						<Button
							type="primary"
							disabled={installment.status === 'paid'}
							onClick={() => showPaymentModal(installment)}
						>
							Init Payment
						</Button>
						{hasPenalty && role === 'admin' && (
							<Button
								type="default"
								danger
								onClick={() => showWaivePenaltyModal(installment)}
							>
								Waive Penalty
							</Button>
						)}
					</div>
				);
			},
		},
	];

	const handleTableChange = (pagination: TablePaginationConfig) => {
		setCurrentPage(pagination.current || 1);
		setPageSize(pagination.pageSize || 10);
	};

	return (
		<div className="space-y-4">
			{role === 'admin' && <ContractAmendmentHistory device={device} />}
			{device.has_overdue_payment && (
				<div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 shadow-sm">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-2">
							<Badge status="error" />
							<Typography.Text
								strong
								type="danger"
							>
								Overdue Payment
							</Typography.Text>
						</div>
						{parseFloat(device.penalty_amount) > 0 && (
							<div>
								<Typography.Text
									strong
									type="danger"
								>
									Penalty Amount: {formatNumber(device.penalty_amount)} UGX
								</Typography.Text>
							</div>
						)}
					</div>
				</div>
			)}

			<Table
				columns={columns}
				dataSource={installments}
				loading={isLoading}
				pagination={{
					current: currentPage,
					pageSize: pageSize,
					total: totalCount,
					showSizeChanger: true,
					showTotal: (total) => `Total ${total} items`,
				}}
				onChange={handleTableChange}
			/>

			<Modal
				title="Send Payment Prompt"
				open={isPaymentModalVisible}
				onCancel={handleCancel}
				footer={null}
			>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleInitPayment}
					initialValues={{
						phone_number: device.user.phone_number,
					}}
				>
					{selectedInstallment && (
						<div className="mb-4 rounded-lg bg-gray-50 p-3">
							<Typography.Text type="secondary">
								Outstanding: <strong>{formatNumber(selectedInstallment.outstanding_amount)} UGX</strong>
							</Typography.Text>
						</div>
					)}
					<Form.Item
						name="phone_number"
						label="Phone Number"
						tooltip="The phone number of the user to send the payment prompt to. Make sure to include the country code."
						rules={[
							{ required: true, message: 'Please enter the phone number' },
							{ pattern: /^[0-9+]+$/, message: 'Please enter a valid phone number' },
						]}
					>
						<Input placeholder="Enter phone number" />
					</Form.Item>
					<Form.Item
						name="amount"
						label="Amount"
						tooltip="Enter the amount to pay. Cannot exceed the outstanding balance."
						rules={[
							{ required: true, message: 'Please enter an amount' },
							{
								validator: (_, value) => {
									if (!selectedInstallment) return Promise.resolve();
									const outstanding = parseFloat(selectedInstallment.outstanding_amount);
									if (value < 500) return Promise.reject(new Error('Minimum amount is UGX 500'));
									if (value > outstanding) return Promise.reject(new Error(`Amount cannot exceed ${formatNumber(outstanding)} UGX`));
									return Promise.resolve();
								},
							},
						]}
					>
						<Input type="number" suffix="UGX" placeholder="Enter amount" />
					</Form.Item>
					<div className="flex justify-end gap-2">
						<Button onClick={handleCancel}>Cancel</Button>
						<Button
							type="primary"
							htmlType="submit"
							loading={isSubmitting}
						>
							Send
						</Button>
					</div>
				</Form>
			</Modal>

			<Modal
				title="Waive Penalty"
				open={isWaivePenaltyModalVisible}
				onCancel={handleWaivePenaltyCancel}
				footer={null}
			>
				<Form
					form={waivePenaltyForm}
					layout="vertical"
					onFinish={handleWaivePenalty}
				>
					{selectedInstallment && (
						<div className="mb-4 rounded-lg bg-gray-50 p-3">
							<Typography.Text type="secondary">
								Current Penalty Amount: <strong>{formatNumber(selectedInstallment.penalty_amount)} UGX</strong>
							</Typography.Text>
						</div>
					)}
					<Form.Item
						name="amount"
						label="Waive Amount"
						tooltip="Enter the amount to waive. This amount cannot exceed the current penalty amount."
						rules={[
							{ required: true, message: 'Please enter the amount to waive' },
							{ pattern: /^\d+(\.\d{1,2})?$/, message: 'Please enter a valid amount' },
							{
								validator: (_, value) => {
									if (!selectedInstallment) return Promise.resolve();
									const maxPenalty = parseFloat(selectedInstallment.penalty_amount);
									const waiveAmount = parseFloat(value);
									if (waiveAmount > maxPenalty) {
										return Promise.reject(new Error(`Amount cannot exceed ${formatNumber(maxPenalty)} UGX`));
									}
									if (waiveAmount <= 0) {
										return Promise.reject(new Error('Amount must be greater than 0'));
									}
									return Promise.resolve();
								},
							},
						]}
					>
						<Input
							placeholder="Enter amount to waive"
							type="number"
							suffix="UGX"
						/>
					</Form.Item>
					<div className="flex justify-end gap-2">
						<Button onClick={handleWaivePenaltyCancel}>Cancel</Button>
						<Button
							type="primary"
							danger
							htmlType="submit"
							loading={isWaivingPenalty}
						>
							Waive Penalty
						</Button>
					</div>
				</Form>
			</Modal>
		</div>
	);
};
